import * as React from "react";
import {
  Box,
  Paper,
  Typography,
  Divider,
  Button,
  Chip,
  IconButton,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  CircularProgress,
} from "@mui/material";
import { useNavigate, useParams, useLocation } from "react-router-dom";

import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AssignmentHeaderInfo from "../../components/assignment/AssignmentHeaderInfo";

import { useTranslation } from "react-i18next";
import { translateDbLabel } from "../../utilities/translateDbLabel";
import { translateBackendMessage } from "../../utilities/translateBackendMessage";

import { fetchExecutionHistory } from "../../services/historyService";
import { fetchNfeHeaders } from "../../services/nfeHeaderService";
import { fetchCteHeaders } from "../../services/cteHeaderService";
import CteHeaderInfo from "../../components/assignment/CteHeaderInfo";

const formatDate = (value) => {
  if (!value) return "";
  const d = typeof value === "string" ? value.slice(0, 10) : value;
  return new Date(d + "T00:00:00").toLocaleDateString("pt-BR");
};

const formatTime = (value) => {
  if (!value) return "";
  // Suporta tanto "HH:mm:ss" direto quanto datetime completo
  if (value.length > 8) return value.substring(11, 19);
  return value;
};

const getStageIcon = (step, status) => {
  // Cores alinhadas com os Chips de status e a borda lateral das linhas
  const color =
    status === "S" ? "#22c55e" : status === "E" ? "#ef4444" : "#cbd5e1";

  const iconProps = { fontSize: "small", sx: { color } };

  // IMPORTANTE: converter para string, pois "step" pode vir como number do backend
  switch (String(step)) {
    case "1":
      return <DescriptionIcon {...iconProps} />;
    case "2":
      return <CheckCircleIcon {...iconProps} />;
    case "3":
      return <ShoppingCartIcon {...iconProps} />;
    case "4":
      return <AssignmentTurnedInIcon {...iconProps} />;
    case "5":
      return <Inventory2Icon {...iconProps} />;
    case "6":
      return <LocalShippingIcon {...iconProps} />;
    case "7":
      return <Inventory2Icon {...iconProps} />;
    case "8":
      return <ReceiptLongIcon {...iconProps} />;
    case "9":
      return <TaskAltIcon {...iconProps} />;
    default:
      return <DescriptionIcon {...iconProps} />;
  }
};

const HistoryPage = () => {
  const { t, i18n } = useTranslation();
  const isEnglish = i18n.language?.startsWith("en");
  const navigate = useNavigate();
  const location = useLocation();
  const { accessKey } = useParams();
  const docType = location.state?.docType === "cte" ? "cte" : "nfe";

  const [rows, setRows] = React.useState([]);
  const [headerInfo, setHeaderInfo] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");

  React.useEffect(() => {
    const loadData = async () => {
      if (!accessKey) return;

      setLoading(true);
      setError("");

      try {
        if (docType === "cte") {
          const cteData = await fetchCteHeaders({ accessKey });
          const cte = cteData?.[0] || null;

          // CT-e não passa pelo motor de etapas ainda
          const mappedRows = cte
            ? [
                {
                  id: `${accessKey}-step1`,
                  step: 1,
                  sequence: 1,
                  description_step: "Documento Criado",
                  execution_date: cte.received_date,
                  execution_time: cte.received_time,
                  user: cte.received_by,
                  status: cte.execution_status,
                },
                {
                  id: `${accessKey}-step2`,
                  step: 2,
                  sequence: 1,
                  description_step: "Validar Assinatura",
                  execution_date: cte.received_date,
                  execution_time: cte.received_time,
                  user: cte.received_by,
                  status: cte.execution_status,
                },
              ]
            : [];

          setRows(mappedRows);
          setHeaderInfo(cte);
        } else {
          const [historyData, headerData] = await Promise.all([
            fetchExecutionHistory(accessKey),
            fetchNfeHeaders({ accessKey }),
          ]);

          const mappedRows = (historyData || []).map((item, index) => ({
            id: item.id ?? `${accessKey}-${index}`,
            ...item,
            sequence: item.sequential_step,
            user: item.username,
            status: item.execution_status,
          }));

          setRows(mappedRows);
          setHeaderInfo(headerData?.[0] || null);
        }
      } catch (err) {
        console.error(err);
        setError(translateBackendMessage(err.message, isEnglish) || t("history.fetchError"));
        setRows([]);
        setHeaderInfo(null);
      } finally {
        setLoading(false);
      }
    };

    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessKey, docType]);

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
        <IconButton
          sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider" }}
          size="small"
          onClick={() => navigate(-1)}
        >
          <ArrowBackIcon fontSize="small" />
        </IconButton>
        <Typography
          variant="h5"
          sx={{ fontWeight: 800, color: "text.primary", letterSpacing: "-0.02em" }}
        >
          {t("history.title")}
          {accessKey ? " - " : ""}
          {accessKey && (
            <Typography
              component="span"
              variant="h5"
              sx={{ color: "primary.main", fontFamily: "monospace", fontWeight: 700 }}
            >
              {accessKey}
            </Typography>
          )}
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "16px",
          overflow: "hidden",
          p: 3,
          mb: 2,
          bgcolor: "background.paper",
        }}
      >
        {headerInfo && (docType === "cte"
          ? <CteHeaderInfo headerInfo={headerInfo} />
          : <AssignmentHeaderInfo headerInfo={headerInfo} />
        )}
      </Paper>

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "16px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          bgcolor: "background.paper",
        }}
      >
        <Box sx={{ px: 3, py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: "text.primary" }}>
            {t("history.subtitle")}
          </Typography>
        </Box>

        {error && (
          <Box
            sx={{
              p: 2,
              m: 2,
              bgcolor: "#fee2e2",
              borderRadius: "8px",
              border: "1px solid #fca5a5",
            }}
          >
            <Typography color="error.main" variant="body2" fontWeight={600}>
              {error}
            </Typography>
          </Box>
        )}

        <TableContainer sx={{ flex: 1, maxHeight: "calc(100vh - 350px)" }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow
                sx={{
                  "& th": {
                    bgcolor: "background.subtle",
                    fontWeight: 700,
                    color: "text.secondary",
                    fontSize: "0.75rem",
                    textTransform: "uppercase",
                    py: 1.5,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  },
                }}
              >
                <TableCell width={50} align="center" />
                <TableCell>{t("columns.step")}</TableCell>
                <TableCell align="center">{t("columns.sequentialStep")}</TableCell>
                <TableCell>{t("columns.executionDate")}</TableCell>
                <TableCell>{t("columns.executionTime")}</TableCell>
                <TableCell>{t("columns.userName")}</TableCell>
                <TableCell align="center">{t("columns.status")}</TableCell>
                <TableCell>{t("columns.errorDescription")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 10 }}>
                    <CircularProgress size={30} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 10 }}>
                    <Typography variant="body2" color="text.secondary">
                      {t("history.empty")}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row) => {
                  const isSuccess = row.status === "S";
                  const isError = row.status === "E";

                  return (
                    <TableRow
                      key={row.id}
                      hover
                      sx={{
                        "&:hover": { bgcolor: "action.hover" },
                        "& td:first-of-type": {
                          borderLeft: `4px solid ${
                            isSuccess ? "#22c55e" : isError ? "#ef4444" : "#cbd5e1"
                          }`,
                        },
                      }}
                    >
                      <TableCell align="center">
                        {getStageIcon(row.step, row.status)}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, color: "text.primary" }}>
                        {translateDbLabel(row.description_step, isEnglish)}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={row.sequence}
                          size="small"
                          sx={{ bgcolor: "background.subtle", fontWeight: 700, color: "text.secondary" }}
                        />
                      </TableCell>
                      <TableCell>{formatDate(row.execution_date)}</TableCell>
                      <TableCell
                        sx={{ fontFamily: "monospace", fontWeight: 600, color: "text.secondary" }}
                      >
                        {formatTime(row.execution_time)}
                      </TableCell>
                      <TableCell>{row.user}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={
                            isSuccess
                              ? t("status.success")
                              : isError
                                ? t("status.error")
                                : t("status.pending")
                          }
                          size="small"
                          sx={{
                            fontWeight: 800,
                            fontSize: "0.7rem",
                            bgcolor: isSuccess ? "#dcfce7" : isError ? "#fee2e2" : "#f1f5f9",
                            color: isSuccess ? "#166534" : isError ? "#991b1b" : "#475569",
                            border: `1px solid ${
                              isSuccess ? "#bbf7d0" : isError ? "#fecaca" : "#e2e8f0"
                            }`,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ color: "#ef4444", fontSize: "0.8rem" }}>
                        {row.error_description || "-"}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "flex-end",
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => navigate(-1)}
            sx={{ border: "1px solid", borderColor: "divider", px: 4, textTransform: "none" }}
          >
            {t("buttons.bClose")}
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default HistoryPage;